import {useTranslation} from "react-i18next";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {FilterContext} from "../../../../../components/filters/DashboardFilterContextProvider.jsx";
import useAuthorityConfiguration from "../../../../../api/utilities/useAuthorityConfiguration.js";
import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import {ErpCalculationApi} from "../../../../../api/authority/generated/index";
import {useLegacyPeriodReportError} from "../../Layout/LegacyPeriodReportBoundary.jsx";
import toast from "react-hot-toast";
import {Chart} from "chart.js";
import {useThemeProvider} from "../../../../../mosaic/utils/ThemeContext.jsx";
import {chartColors} from "../../../../../mosaic/charts/ChartjsConfig.jsx";
import moment from "moment-timezone";
import {getContrastBackgroundColors, getContrastBackgroundHoverColors} from "../../../../../utils/graphColors.js";
import ReportingInfoTooltip from "../../../../../components/ReportingInfoTooltip.jsx";
import {useRenderCurrencySign, useRenderPercentValue} from "../../../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../../../utils/useUserSettings.js";
import ReportingSnapshotStatus from "../../../../admin/ReportingSnapshotStatus.jsx";
import CentreSpendAttributionNotice, {selectedCentreSpendAttributionStatus} from "../../../../admin/CentreSpendAttributionNotice.jsx";
import {isSnapshotPeriodIncomplete, snapshotDateValue, snapshotHasPublishedDays, withSnapshotMissingDays} from "../../../../../utils/reportingSnapshotAvailability.js";
import {mediaSourceQualityBlocksMetrics} from "../../../../../utils/reportingSnapshotAvailability.js";

function requestedDates(since, until) {
    const dates = [];
    const cursor = since.clone();
    const end = until.format('YYYY-MM-DD');
    while (cursor.format('YYYY-MM-DD') <= end) {
        dates.push(cursor.format('YYYY-MM-DD'));
        cursor.add(1, 'day');
    }
    return dates;
}

export function pnoChartData(data, snapshot, since, until, mediaSourceQuality = data?.mediaSourceQuality ?? snapshot?.mediaSourceQuality) {
    if (mediaSourceQualityBlocksMetrics(mediaSourceQuality)) {
        return {...data, pnoTotalByCurrency: [], pnoGranularByCurrency: Array.isArray(data?.pnoGranularByCurrency)
            ? data.pnoGranularByCurrency.map((currency) => ({...currency, pnoEntries: Object.fromEntries(Object.keys(currency?.pnoEntries ?? {}).map((date) => [date, {...currency.pnoEntries[date], pnoNet: null, pnoGross: null}]))}))
            : Object.fromEntries(Object.entries(data?.pnoGranularByCurrency ?? {}).map(([key, currency]) => [key, {...currency, pnoEntries: Object.fromEntries(Object.keys(currency?.pnoEntries ?? {}).map((date) => [date, {...currency.pnoEntries[date], pnoNet: null, pnoGross: null}]))}])),
        };
    }
    if (!isSnapshotPeriodIncomplete(snapshot) || !data) {
        return data;
    }

    const dates = withSnapshotMissingDays(snapshot, requestedDates(since, until));
    const sourceCurrencies = data.pnoGranularByCurrency ?? [];
    const currencyEntries = Array.isArray(sourceCurrencies)
        ? sourceCurrencies.map((value, index) => [String(index), value])
        : Object.entries(sourceCurrencies);
    const transformedCurrencies = currencyEntries.map(([key, currencyData]) => {
        const entries = currencyData?.pnoEntries ?? {};
        return [key, {
            ...currencyData,
            pnoEntries: Object.fromEntries(dates.map((date) => {
                const entry = entries[date];
                return [date, {
                    ...entry,
                    pnoGross: snapshotDateValue(entry?.pnoGross ?? null, snapshot, date),
                    pnoNet: snapshotDateValue(entry?.pnoNet ?? null, snapshot, date),
                }];
            })),
        }];
    });

    return {
        ...data,
        pnoGranularByCurrency: Array.isArray(sourceCurrencies)
            ? transformedCurrencies.map(([, currencyData]) => currencyData)
            : Object.fromEntries(transformedCurrencies),
    };
}

export default function PnoPanel({project, halfSize = false, compositionData = undefined}) {

    let width = halfSize ? 'sm:col-span-6' : 'sm:col-span-4';

    let {t} = useTranslation();
    let {et} = useLegacyPeriodReportError();

    let {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {customerWholesaleRequestFilter, customerWholesale} = useContext(FilterContext);

    let authorityConfiguration = useAuthorityConfiguration();

    let renderPercentValue = useRenderPercentValue();
    let renderCurrencySign = useRenderCurrencySign();

    let[userSettings] = useUserSettings();

    let [loadedTotalPnoData, setLoadedTotalPnoData] = useState(null);
    let [loadedTotalPnoLoading, setLoadedTotalPnoLoading] = useState(true);
    useEffect(() => {
        if (compositionData !== undefined) {
            return;
        }

        let active = true;
        setLoadedTotalPnoLoading(true);
        setLoadedTotalPnoData(null);

        new ErpCalculationApi(authorityConfiguration)
            .shopPerformancePNOTotal({
                projectId: project.id,
                erpFilter: {
                    ...customerWholesaleRequestFilter,
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed ?? [],
                    selectedDomainIds: domainsAllowListed ?? [],
                }
            })
            .then((nextTotalPnoData) => {
                if (!active) {
                    return;
                }

                setLoadedTotalPnoData(nextTotalPnoData);
                setLoadedTotalPnoLoading(false);
            })
            .catch(e => {
                if (!active) {
                    return;
                }

                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [compositionData, project.id, since, until, centresAllowListed, domainsAllowListed, customerWholesaleRequestFilter]);

    let totalPnoData = compositionData === undefined
        ? loadedTotalPnoData
        : compositionData?.total ?? null;
    let totalPnoLoading = compositionData === undefined
        ? loadedTotalPnoLoading
        : compositionData === null;

    /*let [totalWithoutOfflinePnoData, setTotalWithoutOfflinePnoData] = useState(null);
    useEffect(() => {
        new ErpCalculationApi(authorityConfiguration)
            .shopPerformancePNOTotalWithoutOffline({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed ?? [],
                    selectedDomainIds: domainsAllowListed ?? [],
                }
            })
            .then(setTotalWithoutOfflinePnoData)
            .catch(e => et(e).then((message) => { if (message) toast.error(message); }));
    }, [project.id, since, until, centresAllowListed, domainsAllowListed]);*/

    let [loadedChannelPnoData, setLoadedChannelPnoData] = useState(null);
    let [loadedChannelPnoLoading, setLoadedChannelPnoLoading] = useState(true);
    useEffect(() => {
        if (compositionData !== undefined) {
            return;
        }

        let active = true;
        setLoadedChannelPnoLoading(true);
        setLoadedChannelPnoData(null);

        new ErpCalculationApi(authorityConfiguration)
            .shopPerformancePNOChannelsOnly({
                projectId: project.id,
                erpFilter: {
                    ...customerWholesaleRequestFilter,
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed ?? [],
                    selectedDomainIds: domainsAllowListed ?? [],
                }
            })
            .then((nextChannelPnoData) => {
                if (!active) {
                    return;
                }

                setLoadedChannelPnoData(nextChannelPnoData);
                setLoadedChannelPnoLoading(false);
            })
            .catch(e => {
                if (!active) {
                    return;
                }

                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [compositionData, project.id, since, until, centresAllowListed, domainsAllowListed, customerWholesaleRequestFilter]);

    let channelPnoData = compositionData === undefined
        ? loadedChannelPnoData
        : compositionData?.channelsOnly ?? null;

    let channelPnoLoading = compositionData === undefined
        ? loadedChannelPnoLoading
        : compositionData === null;
    const totalSnapshot = compositionData === undefined
        ? loadedTotalPnoData?.snapshot
        : compositionData?.snapshot ?? compositionData?.total?.snapshot;
    const channelSnapshot = compositionData === undefined
        ? loadedChannelPnoData?.snapshot
        : compositionData?.snapshot ?? compositionData?.channelsOnly?.snapshot;
    const totalSnapshotIncomplete = isSnapshotPeriodIncomplete(totalSnapshot);
    const channelSnapshotIncomplete = isSnapshotPeriodIncomplete(channelSnapshot);
    const mediaQualityBlocked = mediaSourceQualityBlocksMetrics(totalPnoData?.mediaSourceQuality ?? totalSnapshot?.mediaSourceQuality);
    const channelsMediaQualityBlocked = mediaSourceQualityBlocksMetrics(channelPnoData?.mediaSourceQuality ?? channelSnapshot?.mediaSourceQuality);
    const showPnoSnapshotStatus = totalSnapshotIncomplete || channelSnapshotIncomplete || mediaQualityBlocked || channelsMediaQualityBlocked;
    const totalSummaryUnavailable = !snapshotHasPublishedDays(totalSnapshot);
    const channelSummaryUnavailable = !snapshotHasPublishedDays(channelSnapshot);
    const centreSpendAttribution = compositionData?.centreSpendAttribution;
    const selectedAttributionIncomplete = !totalPnoLoading && selectedCentreSpendAttributionStatus(centreSpendAttribution, (centresAllowListed ?? []).length > 0) !== null;
    const totalChartData = useMemo(() => pnoChartData(totalPnoData, totalSnapshot, since, until), [totalPnoData, totalSnapshot, since, until]);
    const channelChartData = useMemo(() => pnoChartData(channelPnoData, channelSnapshot, since, until), [channelPnoData, channelSnapshot, since, until]);

    const totalPnoCanvas = useRef(null);
    /*const totalWithoutOfflinePnoCanvas = useRef(null);*/
    const channelsPnoCanvas = useRef(null);

    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';
    const {gridColor, textColor} = chartColors;

    const chartOptions = {
        maintainAspectRatio: false,
        animation: false,
        scales: {
            y: {
                border: {
                    display: false,
                },
                ticks: {
                    color: darkMode ? textColor.dark : textColor.light,
                },
                grid: {
                    color: darkMode ? gridColor.dark : gridColor.light,
                },
            },
            x: {
                border: {
                    display: false,
                },
                ticks: {
                    padding: 10,
                    align: 'center',
                    color: darkMode ? textColor.dark : textColor.light,
                },
                grid: {
                    display: false,
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: (chart) => {
                        return renderPercentValue(chart.raw);
                    }
                },
            },
        }
    };

    useEffect(() => {

        if (channelChartData === null || userSettings === null) {
            return;
        }

        let chart = new Chart(
            channelsPnoCanvas.current,
            {
                type: 'line',
                data: {
                    labels: Object.keys(
                        channelChartData?.pnoGranularByCurrency?.[0]?.pnoEntries ?? {}
                    ).map((key) => moment(key).format('DD.MM.')),

                    datasets: Object.values(channelChartData?.pnoGranularByCurrency ?? []).map((value, index) => {
                        let values = [];

                        for (let pnoEntry of Object.values(value?.pnoEntries ?? [])) {
                            values.push(userSettings?.isMarketing ? pnoEntry.pnoGross : pnoEntry.pnoNet);
                        }

                        return {
                            label: t('dashboard.pno-of-currency', {currency: renderCurrencySign(value.currency)}),
                            backgroundColor: getContrastBackgroundColors()[4 + index],
                            hoverBackgroundColor: getContrastBackgroundHoverColors()[4 + index],
                            data: values,
                        };
                    }),
                },
                options: chartOptions
            });

        return () => {
            chart.destroy();
        };
    }, [project.id, channelChartData, userSettings]);

    useEffect(() => {

        if (totalChartData === null || userSettings === null) {
            return;
        }

        let chart = new Chart(
            totalPnoCanvas.current,
            {
                type: 'line',
                data: {
                    labels: Object.keys(
                        totalChartData?.pnoGranularByCurrency?.[0]?.pnoEntries ?? {}
                    ).map((key) => moment(key).format('DD.MM.')),

                    datasets: Object.values(totalChartData?.pnoGranularByCurrency ?? []).map((value, index) => {
                        let values = [];

                        for (let pnoEntry of Object.values(value?.pnoEntries ?? [])) {
                            values.push(userSettings?.isMarketing ? pnoEntry.pnoGross : pnoEntry.pnoNet);
                        }

                        return {
                            label: t('dashboard.pno-of-currency', {currency: renderCurrencySign(value.currency)}),
                            backgroundColor: getContrastBackgroundColors()[5 + index],
                            hoverBackgroundColor: getContrastBackgroundHoverColors()[5 + index],
                            data: values,
                        };
                    }),
                },
                options: chartOptions
            });

        return () => {
            chart.destroy();
        };
    }, [project.id, totalChartData, userSettings]);

    /*useEffect(() => {
        if (totalWithoutOfflinePnoData === null || userSettings === null) {
            return;
        }

        let chart = new Chart(
            totalWithoutOfflinePnoCanvas.current,
            {
                type: 'line',
                data: {
                    labels: Object.keys(
                        totalWithoutOfflinePnoData?.pnoGranularByCurrency[0]?.pnoEntries ?? {}
                    ).map((key) => moment(key).format('DD.MM.')),

                    datasets: Object.values(totalWithoutOfflinePnoData?.pnoGranularByCurrency ?? []).map((value, index) => {
                        let values = [];

                        for (let pnoEntry of Object.values(value?.pnoEntries ?? [])) {
                            values.push(userSettings?.isMarketing ? pnoEntry.pnoGross : pnoEntry.pnoNet);
                        }

                        return {
                            label: t('dashboard.pno-of-currency', {currency: renderCurrencySign(value.currency)}),
                            backgroundColor: getContrastBackgroundColors()[5 + index],
                            hoverBackgroundColor: getContrastBackgroundHoverColors()[5 + index],
                            data: values,
                        };
                    }),
                },
                options: chartOptions
            });

        return () => {
            chart.destroy();
        };
    }, [project.id, totalWithoutOfflinePnoData, userSettings]);*/

    return [
        <div key="pno-snapshot-status" className={showPnoSnapshotStatus ? "col-span-12" : "hidden"}>
            <ReportingSnapshotStatus snapshots={[totalSnapshot, channelSnapshot]}/>
        </div>,
        <CentreSpendAttributionNotice key="centre-spend-attribution" attribution={centreSpendAttribution} selectedCentreFilter={(centresAllowListed ?? []).length > 0} loading={totalPnoLoading}/>,
        <LoadingComponent
            key={'pno-total'}
            loading={totalPnoLoading || totalPnoData === null || userSettings === null}
            className={
                "comp__roundedBox reporting-surface rounded-lg flex flex-col justify-between col-span-12 " + width +
                " bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700"
            }
        >
            <header
                className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
                <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                    {t('dashboard.total-shop-pno')}
                </h3>
                <ReportingInfoTooltip position="right">
                    <div className="text-xs text-gray-200">
                        {t('tooltip.pno.total')}
                    </div>
                </ReportingInfoTooltip>
            </header>
            <div className="p-5 text-center flex items-center justify-center gap-10 grow-1 shrink-1">
                {totalSummaryUnavailable || selectedAttributionIncomplete || mediaQualityBlocked ? <span className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100">—</span> : Object.values(totalPnoData?.pnoTotalByCurrency ?? []).map((value) => {

                    let pnoValue = userSettings?.isMarketing ? value.pnoGross : value.pnoNet;

                    if (pnoValue <= 0) {
                        return null;
                    }

                    return <span
                        className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                        key={'pno' + value.currency}
                        title={value.currency}
                    >
                        {renderPercentValue(pnoValue)}
                    </span>;
                })}
            </div>
            <div className={'p-2 mt-auto'}>
                <div className="grow">
                    {<canvas ref={totalPnoCanvas}></canvas>}
                </div>
            </div>
        </LoadingComponent>,
        /*<LoadingComponent
            key={'pno-total-without-offline'}
            loading={totalWithoutOfflinePnoData === null || userSettings === null}
            className={
                "comp__roundedBox reporting-surface rounded-lg flex flex-col justify-between col-span-12 " + width +
                " bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700"
            }
        >
            <header
                className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
                <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                    {t('dashboard.total-shop-pno-without-offline')}
                </h3>
                <Tooltip bg="dark" size="md" position="right" className={'inline-flex'}>
                    <div className="text-xs text-gray-200">
                        {t('tooltip.pno.total-without-offline')}
                    </div>
                </Tooltip>
            </header>
            <div className="p-5 text-center flex items-center justify-center gap-10 grow-1 shrink-1">
                {Object.values(totalWithoutOfflinePnoData?.pnoTotalByCurrency ?? []).map((value) => {

                    let pnoValue = userSettings?.isMarketing ? value.pnoGross : value.pnoNet;

                    if (pnoValue <= 0) {
                        return null;
                    }

                    return <span
                        className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                        key={'pno' + value.currency}
                        title={value.currency}
                    >
                        {renderPercentValue(pnoValue)}
                    </span>;
                })}
            </div>
            <div className={'p-2 mt-auto'}>
                <div className="grow">
                    {<canvas ref={totalWithoutOfflinePnoCanvas}></canvas>}
                </div>
            </div>
        </LoadingComponent>,*/
        <LoadingComponent
            key={'pno-channels'}
            loading={channelPnoLoading || channelPnoData === null || userSettings === null}
            className={
                "comp__roundedBox reporting-surface rounded-lg flex flex-col justify-between col-span-12 " + width +
                " bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700"
            }
        >
            <header
                className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
                <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                    {t('dashboard.channels-only-pno')}
                </h3>

                <ReportingInfoTooltip position="left">
                    <div className="text-xs text-gray-200">
                        {t('tooltip.pno.channels')}
                        {customerWholesale && <p className="mt-2">{t('dashboard.customer-wholesale-pno-help')}</p>}
                    </div>
                </ReportingInfoTooltip>
            </header>
            <div className="p-5 text-center flex items-center justify-center gap-10 grow-1 shrink-1">
                {channelSummaryUnavailable || channelsMediaQualityBlocked ? <span className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100">—</span> : Object.values(channelPnoData?.pnoTotalByCurrency ?? []).map((value) => {
                    let pnoValue = userSettings?.isMarketing ? value.pnoGross : value.pnoNet;

                    if (pnoValue <= 0) {
                        return null;
                    }

                    return <span
                        className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                        key={'pno' + value.currency}
                        title={value.currency}
                    >
                        {renderPercentValue(pnoValue)}
                    </span>;
                })}
            </div>
            <div className={'p-2 mt-auto'}>
                <div className="grow">
                    {<canvas ref={channelsPnoCanvas}></canvas>}
                </div>
            </div>
        </LoadingComponent>,
    ];
}
